<template>
  <div class="space-y-4 max-w-2xl">
    <div class="flex items-center gap-3">
      <button class="btn-secondary px-2" type="button" title="Back to vendors" @click="router.push({ name: 'VendorList' })">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      </button>
      <h1 class="text-xl font-bold text-gray-900">Import Vendor</h1>
    </div>

    <div v-if="error" class="card border-red-200 bg-red-50">
      <p class="text-sm font-semibold text-red-700 m-0">This vendor is no longer available in Odoo</p>
      <p class="text-xs text-red-700 mt-1 m-0">{{ error }}</p>
      <router-link :to="{ name: 'VendorCreate' }" class="btn-secondary mt-3 inline-flex">Back to browse</router-link>
    </div>

    <div v-else-if="loading" class="card">
      <p class="text-sm text-gray-500 m-0">Loading vendor from Odoo…</p>
    </div>

    <form v-else-if="odooVendor" class="card space-y-4" @submit.prevent="save">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="md:col-span-2">
          <span class="label">Name</span>
          <p class="text-sm text-gray-900 m-0">{{ odooVendor.displayName }}</p>
        </div>
        <div>
          <span class="label">Email</span>
          <p class="text-sm text-gray-900 m-0">{{ odooVendor.email ?? '—' }}</p>
        </div>
        <div>
          <span class="label">City</span>
          <p class="text-sm text-gray-900 m-0">{{ odooVendor.city ?? '—' }}</p>
        </div>
        <div>
          <span class="label">Country</span>
          <p class="text-sm text-gray-900 m-0">{{ odooVendor.country?.name ?? '—' }}</p>
        </div>
        <div class="md:col-span-2">
          <label class="label" for="alias">Alias (optional)</label>
          <input id="alias" v-model="alias" class="input" placeholder="An internal name to identify this vendor by" />
        </div>
      </div>

      <div v-if="saveError" class="rounded-md border border-red-200 bg-red-50 px-3 py-2" role="alert">
        <p class="text-sm text-red-700 m-0">{{ saveError }}</p>
        <router-link
          v-if="archivedMatch"
          :to="{ name: 'VendorDetail', params: { id: archivedMatch.id } }"
          class="text-sm text-red-700 underline"
        >
          Open the vendor to unarchive it
        </router-link>
      </div>

      <div class="flex gap-3 justify-end pt-2">
        <router-link :to="{ name: 'VendorList' }" class="btn-secondary">Cancel</router-link>
        <button class="btn-primary" type="submit" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { fetchOdooVendorById } from '@/services/odooVendorService';
  import { useVendorStore } from '@/store/vendorStore';
  import type { OdooVendor } from '@/types/odooVendor';
  import type { Vendor } from '@/types/vendor';
  import logger from '@/services/logger';
  import { extractErrorMessage } from '@/utils/apiError';
  import { ArchivedVendorError } from '@/utils/vendorArchive';

  const route = useRoute();
  const router = useRouter();
  const vendorStore = useVendorStore();

  const odooId = Number(route.params.odooId);
  const odooVendor = ref<OdooVendor | null>(null);
  const alias = ref('');
  const loading = ref(true);
  const saving = ref(false);
  const error = ref<string | null>(null);
  const archivedMatch = ref<Vendor | null>(null);
  const saveError = ref<string | null>(null);


  onMounted(async () => {
    if (vendorStore.vendors.length === 0) void vendorStore.fetchVendors();
    try {
      odooVendor.value = await fetchOdooVendorById(odooId);
    } catch (loadError) {
      error.value = extractErrorMessage(loadError);
    } finally {
      loading.value = false;
    }
  });

  async function save() {
    saving.value = true;
    archivedMatch.value = null;
    saveError.value = null;
    try {
      const vendor = await vendorStore.addVendor(odooId, alias.value.trim() || null);
      logger.showSuccess(`${vendor.alias || vendor.name} saved`);
      await router.push({ name: 'VendorList' });
    } catch (caught) {
      if (caught instanceof ArchivedVendorError) {
        archivedMatch.value = caught.vendor;
        saveError.value = caught.message;
      } else {
        // A stale store can miss an archived vendor; the backend's 409 says the same thing.
        saveError.value = extractErrorMessage(caught);
      }
    } finally {
      saving.value = false;
    }
  }
</script>
